import React, { useEffect, useRef, useState } from 'react';
import tw, { css, styled, theme, TwStyle } from 'twin.macro';
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable';
import sanitizeHtml from 'sanitize-html';

interface EditableInputProps {
  id: string;
  maxLength?: number;
  defaultValue?: string;
  placeholder?: string;
  style?: Object;
  styleWrapper?: TwStyle[];
  disabled?: boolean;
  errorMessage?: string;
  editorRows?: number;
  /** Removes all html mark up, when enabled*/
  sanitizeContent?: boolean;
  onChange?: (value: string) => void;
  onError?: (err?: string) => void;
  /** Set key prop to force rerender when child changes (temp bugfix) */
  key: any;
  validationRegex?: RegExp;
  /**
   * Auto-save: hand the words over while they are typed, not only on leaving
   * the box. The parent must then give a `key` that does NOT change with the
   * value (a changing key rebuilds the box and takes the cursor away); a
   * value that changes from elsewhere is taken over below instead.
   */
  storeWhileTyping?: boolean;
}

const EditableInput: React.FC<EditableInputProps> = ({
  id,
  style,
  styleWrapper,
  defaultValue,
  maxLength,
  placeholder,
  disabled = false,
  errorMessage,
  editorRows = 1,
  sanitizeContent = true,
  onChange,
  onError,
  validationRegex,
  storeWhileTyping = false,
}) => {
  const txtRef = useRef<string>(defaultValue ?? '');
  /** What this box last handed over itself (store while typing). */
  const lastHandedOver = useRef<string | undefined>(defaultValue);
  const editableRef = useRef<HTMLInputElement>(null);
  const DEFAULT_EDITOR_HEIGHT = 20;
  const [editorHeight, setEditorHeight] = useState(
    `${DEFAULT_EDITOR_HEIGHT}px`
  );

  const [html, setHtml] = useState(defaultValue ?? '');
  const [isValidLength, setIsValidLength] = useState(true);

  const cleanHTML = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  // A value that changed from ELSEWHERE (AI text, another screen part) is
  // shown; the box's own words coming back from the parent are left alone.
  useEffect(() => {
    if (!storeWhileTyping) return;
    if ((defaultValue ?? '') === (lastHandedOver.current ?? '')) return;
    lastHandedOver.current = defaultValue;
    txtRef.current = defaultValue ?? '';
    setHtml(defaultValue ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const blurHandler = () => {
    if (disabled) return;
    const { current: value } = txtRef;
    if (isValidLength) lastHandedOver.current = cleanHTML(value);
    isValidLength ? onChange?.(cleanHTML(value)) : onError?.();
    if (validationRegex && !validationRegex?.test(value)) {
      onError?.();
      return;
    }
    setEditorHeight(`${DEFAULT_EDITOR_HEIGHT}px`);
  };

  const pasteHandler = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();

    // Extract and sanitize the pasted text
    const pastedText = event.clipboardData.getData('text').replace(/:~:.*/, '');
    const sanitizedText = sanitizeContent
      ? cleanHTML(sanitizeHtml(pastedText))
      : pastedText;

    // Nothing readable on the clipboard: the box keeps its words.
    if (!sanitizedText) return;

    // Validate the pasted text length
    const isValidLengthCheck = maxLength
      ? sanitizedText.length <= maxLength
      : true;

    if (
      !isValidLengthCheck ||
      (validationRegex && !validationRegex?.test(sanitizedText))
    ) {
      onError?.();
      return;
    }

    // Update state with valid sanitized content
    setIsValidLength(isValidLengthCheck);
    txtRef.current = sanitizedText;
    setHtml(sanitizedText);

    if (storeWhileTyping) {
      lastHandedOver.current = sanitizedText;
      onChange?.(sanitizedText);
    }
  };

  const changeHandler = (event: ContentEditableEvent) => {
    if (disabled) return;

    const isClearingInput = event.target.value.length === 0;

    if (maxLength)
      setIsValidLength(
        event.target.value.replace('<br>', '').length <= maxLength
      );

    // Update the input value
    if (isClearingInput || isValidLength) txtRef.current = event.target.value;
    else onError?.();

    // Update the html result
    setHtml(
      sanitizeContent ? sanitizeHtml(event.target.value) : event.target.value
    );

    if (storeWhileTyping) {
      const words = cleanHTML(event.target.value);
      if (!maxLength || words.length <= maxLength) {
        lastHandedOver.current = words;
        onChange?.(words);
      }
    }
  };

  const clickHandler = () => {
    editableRef?.current?.focus?.();
    setEditorHeight(`${editorRows * DEFAULT_EDITOR_HEIGHT}px`);
  };

  return (
    <Wrapper
      isDisabled={disabled}
      isValid={isValidLength}
      inputId={id}
      css={styleWrapper}
      {...(storeWhileTyping ? { 'data-page-undo': '' } : {})}
    >
      <ContentEditable
        id={id}
        innerRef={editableRef as any}
        tagName="p"
        html={html}
        fake-placeholder={placeholder}
        onBlur={blurHandler}
        onChange={changeHandler}
        onClick={clickHandler}
        onPaste={pasteHandler}
        disabled={disabled}
        style={
          style
            ? { ...style, minHeight: editorHeight }
            : {
                color: theme`colors.gray.800`,
                minHeight: editorHeight,
              }
        }
      />
      {errorMessage && !isValidLength && (
        <div tw="absolute right-0 mt-1">
          <p tw="text-red-400 text-xs">{errorMessage}</p>
        </div>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  isDisabled: boolean;
  isValid?: boolean;
  inputId: string;
}>`
  ${tw`cursor-text relative`}

  ${({ isDisabled }) =>
    !isDisabled &&
    tw`
      cursor-pointer
      hover:(ring-2 ring-indigo-400 bg-indigo-50 rounded-sm)
      focus:(ring-2 ring-indigo-400 bg-indigo-50 rounded-sm)
    `}

  ${({ isValid }) =>
    !isValid &&
    tw`
    ring-2 ring-red-400 bg-red-50 rounded-sm
    hover:(ring-2 ring-red-400 bg-red-50 rounded-sm)
    focus:(ring-2 ring-red-400 bg-red-50 rounded-sm)
  `}

  ${({ inputId }) => css`
    // https://stackoverflow.com/a/34846535
    #${inputId}:empty:not(:focus)::before {
      content: attr(fake-placeholder);
    }
  `}
`;

export default EditableInput;
